import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { User, Calendar, Phone, Mail, Camera } from "lucide-react"
import { Card, CardContent } from "../components/ui/card.tsx"
import { ProfileLayout } from "../components/ProfileLayout"
import { getCookie, deleteCookie } from "../utils/cookies"
import api from "../utils/api"
 
export default function ProfilePage() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
 
  useEffect(() => {
    const savedUser = getCookie("user")
    if (!savedUser || savedUser === "undefined") {
      navigate("/")
      return
    }

    let parsed = null
    try { parsed = JSON.parse(savedUser) } catch (e) { navigate("/"); return }

    let mobile = parsed?.mobile || parsed?.phone || parsed?.mobileNo || ""
    if (!mobile) {
      setUser(parsed)
      setLoading(false)
      return
    }
    mobile = mobile.replace(/\D/g, "").replace(/^91/, "")

    api.get(`/apis/web/patientbymobile?mobile=91${mobile}`, {
      headers: {
        "Content-Type": "application/json",
        "middleware-key": "$2b$10$5FnYj95ETCkrL3gK6p4zIeqigdvkpP8In5x.8K009CbaDgMc4G7VG"
      }
    }).then(res => {
      if (res.data?.status === 0 && Array.isArray(res.data.result)) {
        const match = res.data.result.find(p => p.patientmr_no === parsed.patientId) || res.data.result[0]
        if (match) {
          setUser({
            patientId: match.patientmr_no || parsed.patientId || "",
            name: match.patient_name?.trim() || parsed.name || "",
            mobile: match.mobile || parsed.mobile || "",
            gender: match.gender || parsed.gender || "",
            age: match.age || parsed.age || "",
            email: match.email || parsed.email || "",
            dob: match.patient_dob || "",
          })
        } else {
          setUser(parsed)
        }
      } else {
        setUser(parsed)
      }
    }).catch(() => setUser(parsed))
      .finally(() => setLoading(false))
  }, [navigate])
 
  const handleLogout = () => {
    deleteCookie("user")
    deleteCookie("accessToken")
    deleteCookie("refreshToken")
    window.dispatchEvent(new CustomEvent("user-updated", { detail: null }))
    navigate("/")
  }
 
  if (!user || loading) return null
 
  const getInitial = (name) => name?.charAt(0).toUpperCase() || "U"
 
  const formatGender = (g) => {
    if (!g) return "N/A"
    if (g === "M" || g === "Male") return "Male"
    if (g === "F" || g === "Female") return "Female"
    return g
  }

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Profile</h2>
        </div>
 
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <Card className="md:col-span-1">
            <CardContent className="p-6 text-center">
              <div className="relative inline-block mb-4">
                <div className="w-32 h-32 rounded-full bg-primary text-white flex items-center justify-center text-4xl font-bold">
                  {getInitial(user.name)}
                </div>
                <button className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg border">
                  <Camera className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <h2 className="text-xl font-semibold mb-1">{user.name || "User"}</h2>
              <p className="text-gray-600 text-sm">{formatGender(user.gender)}{user.age ? `, ${user.age} Years` : ""}</p>
            </CardContent>
          </Card>
 
          <Card className="md:col-span-2">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-6">Personal Details</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Full Name</p>
                    <p className="font-medium">{user.name || "User"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Gender</p>
                    <p className="font-medium">{formatGender(user.gender)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Age</p>
                    <p className="font-medium">{user.age ? `${user.age} Years` : "N/A"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Patient ID</p>
                    <p className="font-medium">{user.patientId || "N/A"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Contact Number</p>
                    <p className="font-medium">{user.mobile || "N/A"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{user.email || "N/A"}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
    </ProfileLayout>
  )
}